import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { formatInr, purchaseRequiresFreightSettlement } from "../lib/accountsSettlement";
import {
  buildPurchaseFreightPreviewParams,
  defaultFreightSlipForPurchase,
  defaultFreightPayee,
  freightModeHelp,
  FREIGHT_BILLABLE_BASIS_OPTIONS,
  FREIGHT_PAYMENT_MODES,
  FREIGHT_PAYEE_OPTIONS,
  resolveFreightTdsPan,
  DALA_RATE_PER_QTL,
  DALA_WEIGHT_BASIS_OPTIONS,
  purchaseFreightPreviewReady,
  SHORTAGE_ALLOWANCE_MODES,
  shortageAllowanceModeMeta,
} from "../lib/freightSettlement";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Loader2 } from "lucide-react";
import TransporterFreightFields, { TransporterSameAsCompanyField } from "./TransporterFreightFields";

function Field({ label, children, hint, className = "" }) {
  return (
    <div className={`space-y-1 min-w-0 ${className}`}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">{label}</Label>
      {children}
      {hint ? <p className="text-[10px] text-muted-foreground/80 leading-snug">{hint}</p> : null}
    </div>
  );
}

function Section({ title, children, className = "", aside = null }) {
  return (
    <div className={`rounded-lg border border-border/80 bg-background p-4 space-y-3.5 shadow-sm ${className}`}>
      <div className="flex items-center justify-between gap-2 border-b border-border/60 pb-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground/70">{title}</p>
        {aside}
      </div>
      {children}
    </div>
  );
}

/**
 * Purchase freight settlement — ordered blocks before material finalize.
 */
export default function PendingFreightSettlementEditor({
  purchase,
  form,
  basePreview,
  canEdit,
  onUpdate,
  onPatch,
}) {
  const [freightPreview, setFreightPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const truckLoadFreight =
    purchaseRequiresFreightSettlement(purchase) || Boolean(basePreview?.freight_settlement_applicable);
  const freightApplicable =
    truckLoadFreight || Boolean(basePreview?.needs_freight_voucher || basePreview?.freight_settlement);
  const freightRequired = !Boolean(form?.freight_waived);

  const calcMode =
    form?.freight_calc_mode
    || (
      form?.freight_amount !== "" && form?.freight_amount != null
        ? "FIXED"
        : "PER_QTL"
    );

  useEffect(() => {
    if (!freightApplicable || !freightRequired || !form) return;
    const defaults = defaultFreightSlipForPurchase(purchase, basePreview);
    const slip = form.freight_slip || {};
    const driver = String(purchase?.driver_name || purchase?.gate_pass?.driver_name || "").trim();
    const currentOwner = String(slip.truck_owner_name || "").trim();
    const ownerIsDriver = driver && currentOwner && currentOwner.toLowerCase() === driver.toLowerCase();

    // Always correct truck owner if it was wrongly filled with gate-pass driver.
    if (ownerIsDriver && defaults.truck_owner_name && defaults.truck_owner_name !== currentOwner) {
      onPatch({
        freight_slip: {
          ...slip,
          truck_owner_name: defaults.truck_owner_name,
          truck_owner_pan: defaults.truck_owner_pan || slip.truck_owner_pan || "",
        },
      });
      return;
    }

    if (form._freight_route_seeded) return;
    const next = { ...defaults, ...slip };

    // Prefer vehicle-history truck owner from defaults — never gate-pass driver.
    if ((!currentOwner || ownerIsDriver) && defaults.truck_owner_name) {
      next.truck_owner_name = defaults.truck_owner_name;
      if (defaults.truck_owner_pan) next.truck_owner_pan = defaults.truck_owner_pan;
    } else if (!currentOwner) {
      const legacy = String(slip.transporter_owner_name || slip.transporter_name || "").trim();
      const company = String(slip.transport_company_name || "").trim();
      const isDriver = driver && legacy.toLowerCase() === driver.toLowerCase();
      const isCompany = company && legacy.toLowerCase() === company.toLowerCase();
      if (legacy && !isDriver && !isCompany) {
        next.truck_owner_name = legacy;
      }
    }

    // Transporter defaults to same as transport company.
    next.transporter_same_as_company = slip.transporter_same_as_company != null
      ? Boolean(slip.transporter_same_as_company)
      : true;
    if (next.transporter_same_as_company) {
      const companyName = next.transport_company_name || next.transport_company_display_name || "";
      next.transporter_party_id = next.transport_company_party_id || null;
      next.transporter_name = companyName;
      next.transporter_owner_name = companyName;
      next.transporter_display_name = companyName;
      next.transporter_pan = next.transport_company_pan || "";
      if (companyName) {
        next.carrier_name = companyName;
        next.bilty_transporter_name = companyName;
      }
    }

    let changed = !form._freight_slip_seeded && Object.keys(slip).length === 0;
    for (const key of ["load_date", "unload_date", "from_place", "to_place", "truck_owner_name"]) {
      if (!String(slip[key] || "").trim() && defaults[key]) {
        next[key] = defaults[key];
        changed = true;
      }
    }
    if (next.transporter_same_as_company !== slip.transporter_same_as_company
      || next.truck_owner_name !== slip.truck_owner_name
      || next.transporter_party_id !== slip.transporter_party_id) {
      changed = true;
    }
    if (changed || !form._freight_slip_seeded) {
      onPatch({
        freight_slip: next,
        _freight_slip_seeded: true,
        _freight_route_seeded: true,
      });
    } else {
      onPatch({ _freight_route_seeded: true });
    }
  }, [purchase?.id, basePreview, freightApplicable, freightRequired, form?._freight_route_seeded, form?._freight_slip_seeded, form?.freight_slip, purchase?.driver_name]);

  useEffect(() => {
    if (!freightApplicable || !freightRequired || !form || !purchase?.id) {
      setFreightPreview(null);
      return undefined;
    }
    if (!purchaseFreightPreviewReady(form)) {
      setFreightPreview(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      setLoadingPreview(true);
      api
        .get(`/purchases/${purchase.id}/freight-settlement-preview`, {
          params: buildPurchaseFreightPreviewParams(form, basePreview),
          skipDedupe: true,
        })
        .then((r) => {
          if (!cancelled) setFreightPreview(r.data);
        })
        .catch((err) => {
          if (cancelled) return;
          setFreightPreview(null);
          const msg = extractError(err);
          if (err?.response?.status !== 400) {
            toast.error(msg);
          }
        })
        .finally(() => {
          if (!cancelled) setLoadingPreview(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    purchase?.id,
    form,
    freightApplicable,
    freightRequired,
    basePreview?.allocations,
    form?.freight_slip?.deduct_dala,
    form?.freight_slip?.dala_weight_basis,
    form?.freight_billable_basis,
    form?.shortage_allowance_mode,
    form?.shortage_allowance_value,
    form?.freight_rate_per_qtl,
    form?.freight_amount,
    form?.freight_calc_mode,
  ]);

  const settlementPreview =
    freightPreview?.freight_settlement != null
      ? freightPreview.freight_settlement
      : basePreview?.freight_settlement || {};
  const freightShortagePreview = freightPreview?.freight_shortage || {
    shortage_method: settlementPreview.shortage_method,
    threshold_qtl: settlementPreview.threshold_qtl,
    unload_below_threshold: settlementPreview.unload_below_threshold,
    requires_allowance_per_bag: settlementPreview.requires_allowance_per_bag,
    suggested_allowance_mode: settlementPreview.suggested_allowance_mode,
  };
  const requiresPerBagAllowance = Boolean(freightShortagePreview?.requires_allowance_per_bag);

  useEffect(() => {
    if (!freightApplicable || !freightRequired || !requiresPerBagAllowance || !canEdit) return;
    if (!form?.shortage_allowance_mode) {
      onUpdate("shortage_allowance_mode", "GM_PER_BAG");
    }
  }, [
    freightApplicable,
    freightRequired,
    requiresPerBagAllowance,
    canEdit,
    purchase?.id,
    form?.shortage_allowance_mode,
    onUpdate,
  ]);

  const slip = form?.freight_slip || {};
  const setSlip = (field, value) => {
    const next = { ...slip, [field]: value };
    if (field === "party_advance") next.advance = value;
    if (field === "advance") next.party_advance = value;
    if (field === "truck_owner_pan") {
      next.truck_owner_pan = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    }
    // Keep carrier = transport company for Tally receipt details
    const company = next.transport_company_name || next.transport_company_display_name || "";
    if (company) {
      next.carrier_name = company;
      next.bilty_transporter_name = company;
    }
    if (field === "freight_payee" || field === "truck_owner_pan" || field === "payment_mode") {
      next.pan_no = resolveFreightTdsPan(next, purchase);
    }
    onPatch({ freight_slip: next });
    if (field === "freight_rate_per_qtl") onUpdate("freight_rate_per_qtl", value);
  };

  const patchSlip = (nextSlip) => {
    const next = { ...slip, ...nextSlip };
    const company = next.transport_company_name || next.transport_company_display_name || "";
    if (company) {
      next.carrier_name = company;
      next.bilty_transporter_name = company;
    }
    if (!next.freight_payee) {
      next.freight_payee = defaultFreightPayee(next.payment_mode);
    }
    next.pan_no = resolveFreightTdsPan(next, purchase);
    onPatch({ freight_slip: next });
  };

  const setCalcMode = (mode) => {
    if (mode === "FIXED") {
      onPatch({
        freight_calc_mode: "FIXED",
        freight_rate_per_qtl: "",
        freight_slip: { ...slip, freight_rate_per_qtl: "" },
      });
    } else {
      onPatch({
        freight_calc_mode: "PER_QTL",
        freight_amount: "",
      });
    }
  };

  if (!freightApplicable) {
    return null;
  }

  const settlement = settlementPreview;
  const freightShortage = freightShortagePreview;
  const payment = freightPreview?.freight_payment || basePreview?.freight_payment || {};
  const slipMeta = freightPreview?.freight_slip || {};
  const delivery = payment.delivery_condition || slipMeta.delivery_condition || "TRUCK_LOAD";
  const billableBasis = form?.freight_billable_basis || "UNLOAD";
  const shortageMode = form?.shortage_allowance_mode || "";
  const shortageMeta = shortageAllowanceModeMeta(shortageMode);
  const shortageMethod = freightShortage?.shortage_method || "load_vs_unload";
  const visibleShortageModes = requiresPerBagAllowance
    ? SHORTAGE_ALLOWANCE_MODES.filter((o) => o.value === "GM_PER_BAG")
    : SHORTAGE_ALLOWANCE_MODES;

  const deductDala = Boolean(slip.deduct_dala);
  const dalaBasis = slip.dala_weight_basis === "UNLOAD" ? "UNLOAD" : "BILLABLE";
  const dalaQtl =
    settlement.dala_qtl != null
      ? Number(settlement.dala_qtl)
      : dalaBasis === "UNLOAD"
        ? Number(settlement.unload_weight_qtl ?? settlement.our_net_qtl)
        : Number(settlement.unload_billable_qtl);
  const dalaAmount =
    settlement.deduct_dala && settlement.dala != null
      ? Number(settlement.dala)
      : deductDala && Number.isFinite(dalaQtl)
        ? Math.round(dalaQtl * DALA_RATE_PER_QTL * 100) / 100
        : null;

  const companyName =
    slip.transport_company_name
    || slip.transport_company_display_name
    || slip.bilty_transporter_name
    || "";
  const freightPayee = slip.freight_payee || defaultFreightPayee(slip.payment_mode);

  return (
    <div className="space-y-4" data-testid="pending-freight-settlement">
      {/* 1. Freight settlement required */}
      <label className="flex items-start gap-3 rounded-md border border-border bg-muted/30 px-3 py-3 cursor-pointer">
        <Checkbox
          checked={freightRequired}
          disabled={!canEdit}
          onCheckedChange={(checked) => {
            const required = Boolean(checked);
            if (!required) {
              // Persist waive immediately in form state and clear freight inputs so
              // save/TDS no longer treat freight as pending.
              onPatch({
                freight_waived: true,
                freight_finalize: false,
                freight_rate_per_qtl: "",
                freight_amount: "",
                freight_calc_mode: "PER_QTL",
              });
            } else {
              onPatch({ freight_waived: false, freight_finalize: true });
            }
          }}
          className="mt-0.5"
        />
        <span className="text-sm leading-snug">
          <span className="font-medium">Freight settlement required</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            Uncheck when BCPL does not settle transporter freight for this truck (no freight voucher).
          </span>
        </span>
      </label>

      {freightRequired ? (
        <>
          {/* 2. Freight calculation */}
          <Section title="Freight calculation">
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="freight_calc_mode"
                  disabled={!canEdit}
                  checked={calcMode === "PER_QTL"}
                  onChange={() => setCalcMode("PER_QTL")}
                />
                Per qtl
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="freight_calc_mode"
                  disabled={!canEdit}
                  checked={calcMode === "FIXED"}
                  onChange={() => setCalcMode("FIXED")}
                />
                Fixed amount
              </label>
            </div>

            {calcMode === "FIXED" ? (
              <Field label="Fixed freight amount (₹)" hint="Total freight for the truck — ignores weight × rate.">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  className="max-w-xs"
                  value={form?.freight_amount ?? ""}
                  disabled={!canEdit}
                  placeholder="Total ₹"
                  onChange={(e) => onUpdate("freight_amount", e.target.value)}
                />
              </Field>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                <Field label="Freight ₹/qtl">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form?.freight_rate_per_qtl ?? slip.freight_rate_per_qtl ?? ""}
                    disabled={!canEdit}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSlip("freight_rate_per_qtl", v);
                      onUpdate("freight_rate_per_qtl", v);
                    }}
                  />
                </Field>
                <Field
                  label="Calculate freight on"
                  hint="Gross freight = billable qtl × rate (billable = net after bardana)."
                >
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={billableBasis}
                    disabled={!canEdit}
                    onChange={(e) => onUpdate("freight_billable_basis", e.target.value)}
                  >
                    {FREIGHT_BILLABLE_BASIS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
          </Section>

          {/* 3. Truck & route + transport company */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 lg:gap-4 lg:items-stretch">
            <Section
              title="Truck & route"
              aside={
                <span className="font-mono text-[11px] font-semibold tracking-wide text-foreground tabular-nums">
                  {slipMeta.vehicle_number || purchase?.vehicle_number || "—"}
                </span>
              }
            >
              <div className="grid grid-cols-2 gap-3">
                <Field label="Load date">
                  <Input
                    type="date"
                    value={slip.load_date || ""}
                    disabled={!canEdit}
                    onChange={(e) => setSlip("load_date", e.target.value)}
                    className="h-9"
                  />
                </Field>
                <Field label="Unload date">
                  <Input
                    type="date"
                    value={slip.unload_date || ""}
                    disabled={!canEdit}
                    onChange={(e) => setSlip("unload_date", e.target.value)}
                    className="h-9"
                  />
                </Field>
              </div>
              <div className="rounded-md border border-dashed border-border/80 bg-muted/40 p-3">
                <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
                  <Field label="From">
                    <Input
                      value={slip.from_place || ""}
                      disabled={!canEdit}
                      onChange={(e) => setSlip("from_place", e.target.value)}
                      className="h-9 font-medium"
                      placeholder="Party city"
                    />
                  </Field>
                  <span className="pb-2 text-muted-foreground text-sm select-none" aria-hidden>
                    →
                  </span>
                  <Field label="To">
                    <Input
                      value={slip.to_place || ""}
                      disabled={!canEdit}
                      onChange={(e) => setSlip("to_place", e.target.value)}
                      className="h-9 font-medium"
                      placeholder="Factory city"
                    />
                  </Field>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Bilty / LR-RR no.">
                  <Input
                    value={slip.bilty_number || ""}
                    disabled={!canEdit}
                    onChange={(e) => setSlip("bilty_number", e.target.value)}
                    className="h-9"
                    placeholder="Enter bilty no."
                  />
                </Field>
                <Field label="Truck owner name" hint="Vehicle owner from freight history — not the gate-pass driver">
                  <Input
                    value={slip.truck_owner_name || ""}
                    disabled={!canEdit}
                    onChange={(e) => setSlip("truck_owner_name", e.target.value)}
                    className="h-9"
                    placeholder="Owner name"
                  />
                </Field>
                <Field label="Truck owner PAN" className="sm:col-span-2" hint="Used for TDS when freight is paid to the truck owner">
                  <Input
                    value={slip.truck_owner_pan || ""}
                    disabled={!canEdit}
                    onChange={(e) => setSlip("truck_owner_pan", e.target.value)}
                    className="h-9 uppercase max-w-xs"
                    maxLength={10}
                    placeholder="ABCDE1234F"
                  />
                </Field>
              </div>
            </Section>

            <Section title="Transport parties">
              <p className="text-[11px] text-muted-foreground leading-snug -mt-1">
                Three roles: bilty company, transporter who assigned the truck, and truck owner (left).
              </p>
              <TransporterFreightFields
                role="company"
                slip={slip}
                onSlipChange={patchSlip}
                vehicleNumber={slipMeta.vehicle_number || purchase?.vehicle_number || ""}
                ownerName={companyName || ""}
                paymentMode={slip.payment_mode || ""}
                disabled={!canEdit}
                compact
              />
              <TransporterSameAsCompanyField
                slip={slip}
                onSlipChange={patchSlip}
                disabled={!canEdit}
                compact
              />
              {!slip.transporter_same_as_company ? (
                <TransporterFreightFields
                  role="transporter"
                  slip={slip}
                  onSlipChange={patchSlip}
                  vehicleNumber={slipMeta.vehicle_number || purchase?.vehicle_number || ""}
                  ownerName={slip.transporter_owner_name || slip.transporter_name || ""}
                  paymentMode={slip.payment_mode || ""}
                  disabled={!canEdit}
                  compact
                />
              ) : (
                <p className="text-[11px] text-muted-foreground leading-snug pl-0.5">
                  Transporter = transport company for this truck.
                </p>
              )}
            </Section>
          </div>

          {/* 4. Deductions & charges */}
          <Section title="Deductions & charges">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[
                ["other_charges", "Other charges"],
                ["party_advance", "Party advance to transporter"],
                ["clerk_bilty_charge", "Clerk / Bilty"],
                ["other_deductions", "Other deductions"],
              ].map(([key, label]) => (
                <Field key={key} label={label}>
                  <Input
                    type="number"
                    step="0.01"
                    value={slip[key] ?? 0}
                    disabled={!canEdit}
                    onChange={(e) => setSlip(key, e.target.value)}
                  />
                </Field>
              ))}
            </div>

            <div className="rounded-md border border-violet-200 bg-violet-50/40 p-3 space-y-2">
              <p className="text-[11px] text-violet-900/80">{freightModeHelp(delivery)}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Payment mode">
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={slip.payment_mode || "BCPL_PAYS_FULL"}
                    disabled={!canEdit}
                    onChange={(e) => {
                      const mode = e.target.value;
                      const nextPayee =
                        mode === "PARTY_PAID_FULL"
                          ? "PARTY"
                          : (slip.freight_payee === "PARTY" ? "TRANSPORT_COMPANY" : (slip.freight_payee || "TRANSPORT_COMPANY"));
                      patchSlip({ payment_mode: mode, freight_payee: nextPayee });
                    }}
                  >
                    {FREIGHT_PAYMENT_MODES.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
                <Field
                  label="Freight paid to"
                  hint="TDS uses this payee’s PAN"
                >
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={freightPayee}
                    disabled={!canEdit}
                    onChange={(e) => setSlip("freight_payee", e.target.value)}
                  >
                    {FREIGHT_PAYEE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
              {payment && (
                <div className="text-xs text-violet-950 space-y-1">
                  <p>
                    Delivery: <strong>{delivery}</strong> · Pay transporter:{" "}
                    <strong>{formatInr(payment.payable_to_transporter ?? settlement.net_freight_payable)}</strong>
                  </p>
                  {payment.party_bill_amount > 0 && payment.party_bill_effect && (
                    <p>
                      Supplier bill:{" "}
                      <strong>
                        {payment.party_bill_effect === "add" ? "Add" : "Deduct"} {formatInr(payment.party_bill_amount)}
                      </strong>
                      {payment.party_bill_label ? ` — ${payment.party_bill_label}` : ""}
                    </p>
                  )}
                  {payment.note && <p className="text-violet-800/90">{payment.note}</p>}
                </div>
              )}
            </div>

            <div className="rounded-md border border-orange-200 bg-orange-50/40 p-3 space-y-2">
              <p className="text-[11px] font-medium text-orange-950">Truck shortage allowance</p>
              {requiresPerBagAllowance ? (
                <p className="text-[11px] text-orange-950 leading-relaxed">
                  Fixed kg/bag contract: unload weight (
                  {settlement.unload_weight_qtl ?? settlement.our_net_qtl ?? "—"} qtl) is below the expected threshold (
                  {freightShortage.threshold_qtl ?? "—"} qtl). Enter grams per bag tolerance.
                </p>
              ) : shortageMethod === "load_vs_unload" ? (
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Shortage is load weight minus unload weight. Set tolerance before short-goods deduction.
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Tolerance before short-goods value is deducted from transporter.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <Field label="Allowance basis">
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={shortageMode || "_none"}
                    disabled={!canEdit}
                    onChange={(e) => {
                      const v = e.target.value === "_none" ? "" : e.target.value;
                      onUpdate("shortage_allowance_mode", v);
                      if (!v) onUpdate("shortage_allowance_value", "");
                    }}
                  >
                    <option value="_none">None</option>
                    {visibleShortageModes.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
                {shortageMode ? (
                  <Field label={`Allowance (${shortageMeta?.unit || ""})`} hint={shortageMeta?.placeholder}>
                    <Input
                      type="number"
                      step={shortageMode === "GM_PER_BAG" ? "1" : "0.01"}
                      min="0"
                      value={form?.shortage_allowance_value ?? ""}
                      disabled={!canEdit}
                      placeholder={shortageMeta?.placeholder}
                      onChange={(e) => onUpdate("shortage_allowance_value", e.target.value)}
                    />
                  </Field>
                ) : requiresPerBagAllowance ? (
                  <Field label="Allowance (gm/bag)">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      value={form?.shortage_allowance_value ?? ""}
                      disabled={!canEdit}
                      placeholder="e.g. 200"
                      onChange={(e) => onUpdate("shortage_allowance_value", e.target.value)}
                    />
                  </Field>
                ) : null}
              </div>
            </div>
          </Section>

          {/* 5. Deduct dala */}
          <Section title="Deduct dala">
            <label className="flex items-start gap-3 rounded-md border border-dashed border-border px-3 py-3 cursor-pointer">
              <Checkbox
                checked={deductDala}
                disabled={!canEdit}
                onCheckedChange={(checked) => setSlip("deduct_dala", Boolean(checked))}
                className="mt-0.5"
              />
              <span className="text-sm leading-snug flex-1">
                <span className="font-medium">Deduct dala</span>
                <span className="block text-xs text-muted-foreground mt-0.5">
                  ₹{DALA_RATE_PER_QTL}/qtl
                  {dalaAmount != null ? ` = ${formatInr(dalaAmount)}` : ""}
                </span>
              </span>
            </label>
            {deductDala && (
              <Field
                label="Calculate dala on"
                hint="Unload = our weighbridge net. Billable = unload after bardana."
              >
                <select
                  className="flex h-9 w-full max-w-md rounded-md border border-input bg-background px-3 py-1 text-sm"
                  value={dalaBasis}
                  disabled={!canEdit}
                  onChange={(e) => setSlip("dala_weight_basis", e.target.value)}
                >
                  {DALA_WEIGHT_BASIS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>
            )}
          </Section>

          {/* 6. Weight & settlement (TDS controls moved to step 6 — TDS Review) */}
          <div className="rounded-md border border-amber-200 bg-amber-50/40 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-amber-950">Weight &amp; settlement</p>
              {loadingPreview && <Loader2 className="h-3 w-3 animate-spin text-amber-800" />}
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              <dt className="text-muted-foreground">Load weight</dt>
              <dd className="tabular text-right">{settlement.load_weight_qtl ?? settlement.party_net_qtl ?? "—"} qtl</dd>
              <dt className="text-muted-foreground">Unload weight</dt>
              <dd className="tabular text-right">{settlement.unload_weight_qtl ?? settlement.our_net_qtl ?? "—"} qtl</dd>
              <dt className="text-muted-foreground">
                Freight billable ({calcMode === "FIXED" ? "fixed" : billableBasis === "LOAD" ? "load" : "unload"})
              </dt>
              <dd className="tabular text-right font-medium">{settlement.freight_billable_qtl ?? "—"} qtl</dd>
              <dt className="text-muted-foreground">Unload billable (dala)</dt>
              <dd className="tabular text-right">{settlement.unload_billable_qtl ?? "—"} qtl</dd>
              <dt className="text-muted-foreground">Shortage</dt>
              <dd className="tabular text-right">{settlement.shortage_qtl ?? settlement.weight_diff_qtl ?? "—"} qtl</dd>
              <dt className="text-muted-foreground">Allowed shortage</dt>
              <dd className="tabular text-right">{settlement.allowance_qtl ?? "—"} qtl</dd>
              <dt className="text-muted-foreground">Net shortage</dt>
              <dd className="tabular text-right">{settlement.net_shortage_qtl ?? settlement.weight_short_qtl ?? "—"} qtl</dd>
              <dt className="text-muted-foreground">Freight (gross)</dt>
              <dd className="tabular text-right font-medium">{formatInr(settlement.gross_freight)}</dd>
              <dt className="text-muted-foreground">Short goods deduction</dt>
              <dd className="tabular text-right">
                {formatInr(settlement.shortage_deduction ?? settlement.freight_deduction)}
                {settlement.shortage_goods_rate_per_qtl ? (
                  <span className="block text-[10px] text-muted-foreground">
                    {settlement.net_shortage_qtl ?? settlement.weight_short_qtl} qtl × ₹
                    {Number(settlement.shortage_goods_rate_per_qtl).toLocaleString("en-IN")}/qtl
                  </span>
                ) : null}
              </dd>
              <dt className="text-muted-foreground">TDS (1023/1024)</dt>
              <dd className="tabular text-right">
                {formatInr(settlement.tds_amount)}
                {settlement.tds_reason ? (
                  <span className="block text-[10px] text-muted-foreground font-normal">
                    {settlement.tds_reason}
                  </span>
                ) : null}
              </dd>
              {deductDala && dalaAmount != null && dalaAmount > 0 ? (
                <>
                  <dt className="text-muted-foreground">Dala</dt>
                  <dd className="tabular text-right text-red-800">−{formatInr(dalaAmount)}</dd>
                </>
              ) : null}
              <dt className="text-muted-foreground font-semibold text-amber-950">Net payable</dt>
              <dd className="tabular text-right font-semibold text-amber-950">{formatInr(settlement.net_freight_payable)}</dd>
            </dl>
          </div>

          {/* 8. Accounts notes */}
          <Field label="Accounts notes">
            <Textarea
              rows={2}
              value={slip.accounts_note || ""}
              disabled={!canEdit}
              onChange={(e) => setSlip("accounts_note", e.target.value)}
            />
          </Field>

          {form?.freight_finalize !== false && (
            <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border px-3 py-2">
              Freight voucher will be <strong>finalized together</strong> with the material voucher when you click Finalize.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}
